import React from 'react';
import { Typography, Box, Grid } from '@mui/material';
import { Patientv2, Partnerv2 } from '../../types';
import { usePatientContext } from '../SubPatinetProvider';
import { useTranslation } from 'react-i18next';
import { toTitleCase } from '../../utils/commonUtils';
import { formatDateDMY } from '../../utils/dateUtils';

// Compact row field for report with improved styling
const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', py: 0.5, '@media print': { py: 0.2 } }}>
    <Typography 
      component="span"
      sx={{ 
        color: '#6b7280', 
        fontSize: 13, 
        fontWeight: 500,
        minWidth: 95,
        '@media print': { fontSize: 10, minWidth: 75, color: '#555' }
      }}
    >
      {label}
    </Typography>
    <Typography 
      component="span"
      sx={{ 
        color: '#1f2937', 
        fontSize: 13, 
        fontWeight: 600,
        '@media print': { fontSize: 10, color: '#222' }
      }}
    >
      : {value ?? '-'}
    </Typography>
  </Box>
);

// Calculate age from DOB
const calculateAge = (dob?: string) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  return `${age} Yrs`;
};

const PatientDetailsHeader: React.FC = () => {
  const { subPatientData, patientId } = usePatientContext();
  const { t: tGeneral } = useTranslation('general');

  if (!subPatientData || subPatientData.length === 0) return null;

  const main = subPatientData[0];
  const partner = main.partner;

  let patientToShow: Patientv2 | null = null;
  let partnerToShow: Partnerv2 | null = null;

  if (patientId === main.id) {
    patientToShow = main;
    partnerToShow = partner;
  } else if (partner && patientId === partner.id) {
    // Show partner as patient, main as partner
    patientToShow = {
      ...partner,
      partner: main,
      addresses: Array.isArray(partner.addresses) ? partner.addresses : [],
      additional_info: partner.additional_info ?? null,
      age: main.age,
      updated_on: main.updated_on,
      donor_details: main.donor_details,
      clinic: main.clinic,
      donor_info: main.donor_info,
      photo: partner.photo,
      reason_for_visit: partner.additional_info?.reason_for_visit || '',
      patient: null,
    } as unknown as Patientv2;
    partnerToShow = main as unknown as Partnerv2;
  } else {
    patientToShow = main;
    partnerToShow = partner;
  }

  if (!patientToShow) return null;

  return (
    <Box 
      sx={{ 
        mt: 2,
        mb: 1.5,
        py: 1.25,
        px: 2,
        backgroundColor: '#f8fafc',
        borderRadius: 1,
        border: '1px solid #e5e7eb',
        borderLeft: '4px solid #3b82f6',
        '@media print': {
          mt: 0,
          mb: 0.5,
          py: 0.5,
          px: 0,
          backgroundColor: 'transparent',
          border: 'none',
          borderLeft: 'none',
          borderBottom: '1px solid #ddd',
          borderRadius: 0
        }
      }}
    >
      <Grid container spacing={0}>
        {/* Patient Section - Left Half */}
        <Grid item xs={partnerToShow ? 6 : 12} sx={{ pr: partnerToShow ? 2.5 : 0 }}>
          <Grid container spacing={0}>
            <Grid item xs={6}>
              <FieldRow label={tGeneral('patientName')} value={`${patientToShow.first_name} ${patientToShow.last_name}`} />
              <FieldRow label={tGeneral('patientId')} value={patientToShow.medical_record_no || '-'} />
              <FieldRow label={tGeneral('DOB')} value={formatDateDMY(patientToShow.date_of_birth)} />
            </Grid>
            <Grid item xs={6}>
              <FieldRow label={`${tGeneral('gender')} / Age`} value={`${toTitleCase(patientToShow.gender) || '-'} / ${calculateAge(patientToShow.date_of_birth)}`} />
              <FieldRow label={tGeneral('mobileNo')} value={patientToShow.mobile_number || '-'} />
            </Grid>
          </Grid>
        </Grid>

        {/* Partner Section - Right Half */}
        {partnerToShow && (
          <Grid item xs={6} sx={{ pl: 2.5, borderLeft: '2px solid #d1d5db', '@media print': { borderColor: '#ccc', borderLeftWidth: '1px', pl: 1.5 } }}>
            <Grid container spacing={0}>
              <Grid item xs={6}>
                <FieldRow label={tGeneral('partner')} value={`${partnerToShow.first_name} ${partnerToShow.last_name}`} />
                <FieldRow label={tGeneral('partnerId')} value={partnerToShow.medical_record_no || '-'} />
                <FieldRow label={tGeneral('partnerDOB')} value={formatDateDMY(partnerToShow.date_of_birth)} />
              </Grid>
              <Grid item xs={6}>
                <FieldRow label={`${tGeneral('gender')} / Age`} value={`${toTitleCase(partnerToShow.gender) || '-'} / ${calculateAge(partnerToShow.date_of_birth)}`} />
                <FieldRow label={tGeneral('mobileNo')} value={partnerToShow.mobile_number || '-'} />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PatientDetailsHeader;
